import React from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';
import type { WorkoutProgressResponse, ExerciseProgressResponse, BatchSuccessRateEntry } from './types';

interface WorkoutHistoryTableProps {
  viewMode: 'all' | 'exercise';
  workoutProgress: WorkoutProgressResponse | null;
  exerciseProgress: ExerciseProgressResponse | null;
  successRates: BatchSuccessRateEntry[];
}

const WorkoutHistoryTable: React.FC<WorkoutHistoryTableProps> = ({
  viewMode, workoutProgress, exerciseProgress, successRates,
}) => {
  return (
    <Paper className="no-print" sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>
        История на Тренировки
      </Typography>

      {viewMode === 'all' && workoutProgress?.workouts && workoutProgress.workouts.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[...workoutProgress.workouts].reverse().map((workout) => {
            const rate = successRates.find(r => r.sessionId === workout.id);
            const hasAssistance = workout.exercises.some(ex => ex.sets.some(s => s.assistance));
            return (
              <Box key={workout.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {workout.name} — {new Date(workout.date).toLocaleDateString('bg-BG')}
                  </Typography>
                  {workout.adaptationStatus && (
                    <Chip
                      label={workout.adaptationStatus === 'applied' ? 'AI адаптация' : 'Без AI адаптация'}
                      size="small"
                      color={workout.adaptationStatus === 'applied' ? 'info' : 'default'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {rate && (
                    <Chip
                      label={`${Math.round(rate.successRate * 100)}%`}
                      size="small"
                      color={rate.successRate > 0.8 ? 'success' : rate.successRate > 0.5 ? 'warning' : 'error'}
                      sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  {workout.checkInData && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      Check-in: {workout.checkInData.availableTimeMinutes} мин, умора {workout.checkInData.fatigueLevel}/10, сън {workout.checkInData.sleepQuality}/10
                    </Typography>
                  )}
                  {workout.adaptationSummary?.appliedChanges && workout.adaptationSummary.appliedChanges.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      AI промени: {workout.adaptationSummary.appliedChanges.length} приложени
                    </Typography>
                  )}
                  {workout.notes && (
                    <Typography variant="body2" sx={{ display: 'block', mb: 1, whiteSpace: 'pre-wrap' }}>
                      Допълнителна информация: {workout.notes}
                    </Typography>
                  )}
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #555' }}>
                        <th style={{ padding: '5px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Упражнение</th>
                        <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Сет</th>
                        <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Повт.</th>
                        <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Тегло (kg)</th>
                        <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>RPE</th>
                        {hasAssistance && (
                          <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Асист.</th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {workout.exercises.map((ex, exIdx) =>
                        ex.sets.map((set, setIdx) => (
                          <tr key={`${exIdx}-${setIdx}`} style={{
                            borderBottom: setIdx === ex.sets.length - 1 ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.05)',
                          }}>
                            {setIdx === 0 ? (
                              <td rowSpan={ex.sets.length} style={{
                                padding: '4px 8px', fontWeight: 600, verticalAlign: 'top',
                                borderRight: '1px solid rgba(255,255,255,0.1)', whiteSpace: 'nowrap',
                              }}>
                                {ex.exerciseName}
                              </td>
                            ) : null}
                            <td style={{ padding: '3px 8px', textAlign: 'center' }}>{setIdx + 1}</td>
                            <td style={{ padding: '3px 8px', textAlign: 'center', fontWeight: 600 }}>{set.repsLeft != null && set.repsRight != null ? `Л:${set.repsLeft} / Д:${set.repsRight}` : set.reps}</td>
                            <td style={{ padding: '3px 8px', textAlign: 'center', fontWeight: 600 }}>{set.weight}</td>
                            <td style={{ padding: '3px 8px', textAlign: 'center' }}>{set.rpe ?? '-'}</td>
                            {hasAssistance && (
                              <td style={{ padding: '3px 8px', textAlign: 'center' }}>{set.assistance ? 'Да' : '-'}</td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {viewMode === 'exercise' && exerciseProgress?.workouts && exerciseProgress.workouts.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #555' }}>
                <th style={{ padding: '5px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Дата</th>
                <th style={{ padding: '5px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Тренировка</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Сет</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Повт.</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Тегло (kg)</th>
                <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>RPE</th>
                {exerciseProgress.workouts.some(w => w.sets.some(s => s.assistance)) && (
                  <th style={{ padding: '5px 8px', textAlign: 'center', whiteSpace: 'nowrap' }}>Асист.</th>
                )}
              </tr>
            </thead>
            <tbody>
              {[...exerciseProgress.workouts].reverse().map((workout, wIdx) =>
                workout.sets.map((set, setIdx) => (
                  <tr key={`${wIdx}-${setIdx}`} style={{
                    borderBottom: setIdx === workout.sets.length - 1 ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.05)',
                  }}>
                    {setIdx === 0 ? (
                      <>
                        <td rowSpan={workout.sets.length} style={{
                          padding: '4px 8px', fontWeight: 600, verticalAlign: 'top', whiteSpace: 'nowrap',
                        }}>
                          {new Date(workout.date).toLocaleDateString('bg-BG')}
                        </td>
                        <td rowSpan={workout.sets.length} style={{
                          padding: '4px 8px', verticalAlign: 'top',
                          borderRight: '1px solid rgba(255,255,255,0.1)', whiteSpace: 'nowrap',
                        }}>
                          {workout.workoutName}
                          {workout.notes && (
                            <div style={{ marginTop: '4px', whiteSpace: 'normal', maxWidth: '260px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)' }}>
                              Доп. инфо: {workout.notes}
                            </div>
                          )}
                        </td>
                      </>
                    ) : null}
                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>{setIdx + 1}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'center', fontWeight: 600 }}>{set.repsLeft != null && set.repsRight != null ? `Л:${set.repsLeft} / Д:${set.repsRight}` : set.reps}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'center', fontWeight: 600 }}>{set.weight}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'center' }}>{set.rpe ?? '-'}</td>
                    {exerciseProgress.workouts.some(w => w.sets.some(s => s.assistance)) && (
                      <td style={{ padding: '3px 8px', textAlign: 'center' }}>{set.assistance ? 'Да' : '-'}</td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </Box>
      )}
    </Paper>
  );
};

export default WorkoutHistoryTable;
